import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Textarea } from '../../ui-v2.5';
import { Button } from '../../ui-v2';
import { cancelarCitaGestion } from '../api';
import { opcionesCancelacion, MOTIVO_CANCELAR_MAX } from './opcionesCancelacion';

/**
 * Cancelar una cita abierta (3/11, B1). UN modal, el mismo en el panel del técnico y en el icono de
 * cancelar del resumen de la campaña: POST /citas/:id/cancelacion, que deja pasar al técnico de la
 * cita o a quien gestiona (el servidor lo decide; esto sólo pregunta).
 *
 * Tres opciones (opcionesCancelacion.js): por el usuario, por sistemas, no asistió (sólo si la hora
 * ya llegó). Motivo opcional, texto plano, hasta 500 caracteres.
 *
 * `cita`: { id, fecha, hora_inicio, hora_fin } (null = cerrado). `persona`: nombre para el subtítulo.
 * `onHecho(resultado)` tras cancelar; quien lo abre recarga.
 */
const fechaLarga = (s) => (s
  ? new Date(`${s}T00:00:00`).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
  : '—');

export default function ModalCancelarCita({ cita, persona, onClose, onHecho }) {
  const [tipo, setTipo] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const citaId = cita ? cita.id : null;

  // Cada vez que se abre para otra cita, empieza de cero.
  useEffect(() => { setTipo(null); setMotivo(''); setError(null); setEnviando(false); }, [citaId]);

  const opciones = useMemo(() => (cita ? opcionesCancelacion(cita) : []), [cita]);

  async function confirmar() {
    if (!tipo || enviando) return;
    setEnviando(true); setError(null);
    try {
      const r = await cancelarCitaGestion(cita.id, tipo, motivo.trim() || null);
      onHecho(r?.data || r);
    } catch (e) {
      setError(e?.response?.data?.error || 'No se ha podido cancelar la cita.');
      setEnviando(false);
    }
  }

  return (
    <Modal
      open={!!cita}
      onClose={enviando ? undefined : onClose}
      size="md"
      title="Cancelar la cita"
      subtitle={cita ? `${persona || 'Persona'} · ${fechaLarga(cita.fecha)}${cita.hora_inicio ? ` · ${cita.hora_inicio}–${cita.hora_fin || ''}` : ''}` : ''}
      footer={(
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={enviando}>Volver</Button>
          <Button type="button" variant="destructive" onClick={confirmar} disabled={!tipo || enviando}>
            {enviando ? 'Guardando…' : 'Confirmar'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <fieldset className="space-y-2">
          <legend className="mb-2 text-xs font-medium text-slate-700">¿Qué ha pasado?</legend>
          {opciones.map((o) => {
            const elegida = tipo === o.tipo;
            return (
              <label
                key={o.tipo}
                className={
                  'flex items-start gap-3 rounded-lg border p-3 text-sm transition-colors '
                  + (!o.disponible
                    ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-500'
                    : elegida
                      ? 'cursor-pointer border-slate-900 bg-white'
                      : 'cursor-pointer border-slate-200 bg-white hover:border-slate-400')
                }
              >
                <input
                  type="radio"
                  name="tipo-cancelacion"
                  value={o.tipo}
                  checked={elegida}
                  disabled={!o.disponible || enviando}
                  onChange={() => setTipo(o.tipo)}
                  className="mt-0.5"
                />
                <span className="min-w-0">
                  <span className="block font-medium text-slate-900">{o.label}</span>
                  <span className="block text-xs text-slate-600">{o.porque || o.descripcion}</span>
                </span>
              </label>
            );
          })}
        </fieldset>

        <Textarea
          label="Motivo (opcional)"
          rows={2}
          maxLength={MOTIVO_CANCELAR_MAX}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          hint={`${motivo.length}/${MOTIVO_CANCELAR_MAX} · puede aparecer en el aviso a la persona y al técnico.`}
          disabled={enviando}
        />

        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{error}</div>
        )}
      </div>
    </Modal>
  );
}
